import { useState } from "react";
import { Building2, Sparkles } from "lucide-react";
import { SearchInput } from "@/components/molecules/SearchInput";
import { useSettingsStore } from "@/store/settingsStore";
import { useProfileStore } from "@/store/profileStore";

export function TenantSelectorModal() {
  const [query, setQuery] = useState("");
  const { profileData } = useProfileStore();
  const { legalEntityId, setLegalEntity, setBranch } = useSettingsStore();

  const isMasterUser = profileData?.userType === "ATLAS_MASTER_USER";
  const hasNoSelection = !legalEntityId;
  const showModal = isMasterUser && hasNoSelection && profileData && profileData.legalEntities.length > 0;

  if (!showModal) return null;

  const rootLegalEntities = profileData?.legalEntities ?? [];
  const q = query.trim().toLowerCase();
  const filteredLegalEntities = q
    ? rootLegalEntities.filter(
        (le) =>
          le.name.toLowerCase().includes(q) ||
          (le.primaryEmail?.toLowerCase().includes(q) ?? false),
      )
    : rootLegalEntities;

  return (
    <div className="fixed inset-0 z-[100] bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-premium overflow-hidden border border-slate-200 flex flex-col max-h-[85vh]">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-bridge-accent/10 rounded-lg">
              <Sparkles className="text-bridge-accent" size={20} />
            </div>
            <h2 className="text-lg font-bold text-slate-800">Select Account</h2>
          </div>
          <p className="text-sm text-slate-500">
            Welcome, Master User. Please select a root legal entity to manage its account and sub-entities.
          </p>
          <div className="mt-4">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search by name or email..."
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/30">
          {filteredLegalEntities.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              No legal entities match your search.
            </p>
          ) : (
            filteredLegalEntities.map((le) => (
              <button
                key={le.id}
                onClick={() => {
                  setLegalEntity(le.id, le.name);
                  setBranch(null, null);
                }}
                className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white hover:border-bridge-accent/50 hover:shadow-sm transition-all group text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-bridge-accent/10 group-hover:text-bridge-accent transition-colors">
                    <Building2 size={18} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 group-hover:text-bridge-accent transition-colors">
                      {le.name}
                    </h3>
                    {le.primaryEmail && (
                      <p className="text-xs text-slate-500 mt-0.5">{le.primaryEmail}</p>
                    )}
                  </div>
                </div>
                <div className="text-xs font-medium text-bridge-accent opacity-0 group-hover:opacity-100 transition-opacity">
                  Select →
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
